/**
 * Auth context — provides user info, token, login, logout, and granular permissions.
 *
 * Token is stored in localStorage under key 'bl_token'.
 * Payload is decoded client-side (no signature verify — the API enforces on every request).
 * Expired tokens are cleared on read.
 *
 * Owner accounts always have all permissions (baked in at JWT signing time).
 * All other accounts have their permission set stored in the JWT perms object.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'

// ── permission types ──────────────────────────────────────────────────────────

export interface Permissions {
  pulse:        boolean
  ask:          boolean
  cash:         boolean
  ships:        boolean
  ships_profit: boolean
  customers:    boolean
  pricing:      boolean
  affiliates:   boolean
  invoices:     boolean
  recon:        boolean
  disputes:     boolean
  claims:       boolean
  personnel:    boolean
  costs:        boolean
  users:        boolean
  uploads:      boolean
  exports:      boolean
  settings:     boolean
  // action permissions (consequential writes)
  manage_rebills:      boolean
  void_invoices:       boolean
  manage_billing_mode: boolean
  edit_cash:           boolean
  upload_files:        boolean
  resolve_disputes:    boolean
  manage_claims:       boolean
}

export type PermKey = keyof Permissions

export const ALL_PERMISSIONS: Permissions = {
  pulse: true, ask: true, cash: true, ships: true, ships_profit: true,
  customers: true, pricing: true, affiliates: true, invoices: true,
  recon: true, disputes: true, claims: true, personnel: true, costs: true,
  users: true, uploads: true, exports: true, settings: true,
  manage_rebills: true, void_invoices: true, manage_billing_mode: true,
  edit_cash: true, upload_files: true, resolve_disputes: true, manage_claims: true,
}

export const NO_PERMISSIONS: Permissions = {
  pulse: false, ask: false, cash: false, ships: false, ships_profit: false,
  customers: false, pricing: false, affiliates: false, invoices: false,
  recon: false, disputes: false, claims: false, personnel: false, costs: false,
  users: false, uploads: false, exports: false, settings: false,
  manage_rebills: false, void_invoices: false, manage_billing_mode: false,
  edit_cash: false, upload_files: false, resolve_disputes: false, manage_claims: false,
}

// ── auth types ────────────────────────────────────────────────────────────────

export interface AuthUser {
  sub:   string
  email: string
  role:  'owner' | 'admin' | 'member'
  name:  string
  tz:    string       // IANA timezone name, e.g. 'America/New_York'
  perms: Permissions
  exp:   number
  // "View as": the owner looking at this user's account (read-only token)
  imp?:  { sub: string; email: string; name: string; log_id: number }
}

export interface AuthState {
  user:     AuthUser | null
  token:    string | null
  timezone: string    // IANA timezone — use for date display + API queries
  isAdmin:  boolean   // role is 'admin' or 'owner'
  isOwner:  boolean   // role is 'owner'
  can:      (perm: PermKey) => boolean
  login:    (email: string, password: string) => Promise<{ ok: true } | { ok: false; error: string }>
  logout:   () => void
  setToken: (token: string) => void
  viewingAs:   boolean   // true while the owner is in "View as" (read-only)
  startViewAs: (userId: number) => Promise<{ ok: true } | { ok: false; error: string }>
  stopViewAs:  () => Promise<void>
}

// ── helpers ───────────────────────────────────────────────────────────────────

// Exported so lib/api-fetch.ts reads the same key. Two copies of this string
// would drift and the global fetch gate would silently stop attaching tokens.
export const TOKEN_KEY = 'bl_token'
// While the owner is in "View as", their own token waits here and the
// read-only view-as token sits in TOKEN_KEY. Only restoreOwnerSession() (at
// boot and on Exit) puts the owner's token back - never tokenIfValid().
export const OWNER_TOKEN_KEY = 'bl_owner_token'

function decodeToken(token: string): AuthUser | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const padded = parts[1] + '='.repeat((4 - (parts[1].length % 4)) % 4)
    const payload = JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')))
    if (!payload.exp || payload.exp < Math.floor(Date.now() / 1000)) return null
    // Back-compat: old tokens had fin:boolean instead of perms object
    if (!payload.perms && typeof payload.fin === 'boolean') {
      const fin = payload.fin as boolean
      payload.perms = fin ? ALL_PERMISSIONS : NO_PERMISSIONS
    }
    if (!payload.perms) payload.perms = NO_PERMISSIONS
    // Back-compat: old tokens didn't carry tz
    if (!payload.tz) payload.tz = 'America/New_York'
    return payload as AuthUser
  } catch {
    return null
  }
}

function loadToken(): { token: string | null; user: AuthUser | null } {
  try {
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) return { token: null, user: null }
    const user = decodeToken(token)
    if (!user) { localStorage.removeItem(TOKEN_KEY); return { token: null, user: null } }
    return { token, user }
  } catch {
    return { token: null, user: null }
  }
}

/**
 * Ends a "View as" in this browser: drops the view-as token and puts the
 * owner's own token back. Called ONLY at boot and from stopViewAs() — never
 * from tokenIfValid(), so an expiring view-as token can never be swapped for
 * the owner's full token underneath a page that still shows the viewed user.
 */
function restoreOwnerSession(): { token: string | null; user: AuthUser | null } {
  try {
    const owner = localStorage.getItem(OWNER_TOKEN_KEY)
    if (!owner) return loadToken()
    const current = loadToken()
    if (current.user && current.user.imp) return current      // still viewing: keep it
    localStorage.removeItem(OWNER_TOKEN_KEY)
    if (current.user) return current                          // someone else's session: never replace it
    const ownerUser = decodeToken(owner)
    if (!ownerUser || ownerUser.imp) return current
    localStorage.setItem(TOKEN_KEY, owner)
    return { token: owner, user: ownerUser }
  } catch {
    return { token: null, user: null }
  }
}

/**
 * The stored token if present and unexpired, else null. An invalid token is
 * cleared, exactly as loadToken() does on boot.
 *
 * Exists for lib/api-fetch.ts, which runs outside React and so cannot read the
 * auth context. Kept here rather than reimplemented there so there is one
 * definition of "is this token usable" — a second copy would drift from
 * decodeToken() and the two would disagree about expiry.
 */
export function tokenIfValid(): string | null {
  return loadToken().token
}

// ── context ───────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const initial = restoreOwnerSession()
  const [token, setTokenState] = useState<string | null>(initial.token)
  const [user,  setUser]       = useState<AuthUser | null>(initial.user)

  const setToken = useCallback((newToken: string) => {
    const decoded = decodeToken(newToken)
    if (!decoded) return
    localStorage.setItem(TOKEN_KEY, newToken)
    setTokenState(newToken)
    setUser(decoded)
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    try {
      const res = await fetch('/api/auth/login', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email, password }),
      })
      const data = await res.json()
      if (!res.ok) return { ok: false as const, error: data.error ?? 'Login failed' }
      // a fresh login ends any "View as" left in this browser
      localStorage.removeItem(OWNER_TOKEN_KEY)
      setToken(data.token)
      return { ok: true as const }
    } catch {
      return { ok: false as const, error: 'Network error' }
    }
  }, [setToken])

  const startViewAs = useCallback(async (userId: number) => {
    if (!token || user?.role !== 'owner' || user.imp) return { ok: false as const, error: 'owner only' }
    try {
      const res = await fetch(`/api/auth/view-as/${userId}`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.token) return { ok: false as const, error: data.error ?? `HTTP ${res.status}` }
      localStorage.setItem(OWNER_TOKEN_KEY, token)
      setToken(data.token)
      return { ok: true as const }
    } catch {
      return { ok: false as const, error: 'Network error' }
    }
  }, [token, user, setToken])

  const stopViewAs = useCallback(async () => {
    if (token && user?.imp) {
      await fetch('/api/auth/view-as/end', {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {/* the log row just stays open */})
    }
    localStorage.removeItem(TOKEN_KEY)
    const restored = restoreOwnerSession()   // puts the owner's token back
    setTokenState(restored.token)
    setUser(restored.user)
  }, [token, user])

  const logout = useCallback(() => {
    if (token && user?.imp) {
      // close the view-as log row too
      fetch('/api/auth/view-as/end', {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {/* ignore */})
    }
    localStorage.removeItem(OWNER_TOKEN_KEY)
    localStorage.removeItem(TOKEN_KEY)
    setTokenState(null)
    setUser(null)
    fetch('/api/auth/logout', {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).catch(() => {/* ignore */})
  }, [token, user])

  // Another tab started / ended a "View as", logged in or out: the token this
  // tab's requests carry (read from localStorage) no longer matches what this
  // tab shows. Reload so the screen and the token agree again - otherwise a
  // tab still showing the viewed user could send the owner's full token.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === TOKEN_KEY || e.key === OWNER_TOKEN_KEY || e.key === null) window.location.reload()
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // Owner always has all permissions regardless of perms object
  const can = useCallback((perm: PermKey): boolean => {
    if (!user) return false
    if (user.role === 'owner') return true
    return user.perms?.[perm] ?? false
  }, [user])

  const value: AuthState = {
    user,
    token,
    timezone: user?.tz ?? 'America/New_York',
    isAdmin:  user?.role === 'admin' || user?.role === 'owner',
    isOwner:  user?.role === 'owner',
    can,
    login,
    logout,
    setToken,
    viewingAs: !!user?.imp,
    startViewAs,
    stopViewAs,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// ── hooks ─────────────────────────────────────────────────────────────────────

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

/**
 * Returns the Authorization header object to pass to fetch.
 * Usage:  fetch('/api/foo', { headers: authHeaders(token) })
 */
export function authHeaders(token: string | null): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {}
}
